import SwiperCore, { Pagination, Navigation } from 'swiper/core';
import { SchemaType } from '@/components/Internals/InternalSchema';
import Layout from '@/components/Layout';
import { AdContainer } from '@/components/Layout/ItemListLastNews/styles';
import { api, site } from '@/services/api';
import { FeaturedNewsApi, HeadTags, NavBarContentData } from '@/shared/types/home.types';
import getConcurrentPromises from '@/utils/getConcurrentPromises';
import { GetServerSideProps } from 'next';
import dynamic from 'next/dynamic';
import { NavBarContent } from '@/components/Navbar';
import Breadcrumb, { BreadcrumbItem } from '@/components/Layout/Breadcrumb';
import Titulo from '@/components/Layout/Title';
import WrapperContent from '@/components/Layout/WrapperContent';
import PlaylistCard from '@/components/Listing/PlaylistCard';
import { PlaylistCardList } from '@/components/Listing/PlaylistCard/styles';

SwiperCore.use([Navigation, Pagination]);

type PlaylistType = {
  playlist: {
    titulo: string;
    url: string;
  };
  video: {
    titulo: string;
    descricao: string;
    url: string;
  };
  corPrincipal?: string;
  logo: {
    url: string;
    blurhash: string;
  };
  thumbnail: {
    url: string;
    blurhash: string;
  };
};

export interface VideoPlaylistsProps {
  homeData: {
    status: number;
    data: {
      playlists: PlaylistType[];
    };
    breadcrumb: BreadcrumbItem[];
  };
  navBarData: NavBarContent;
  tagsData: HeadTags[];
  segmentacoes: [];
  schema?: SchemaType;
}

export default function VideoPlaylists({ homeData, navBarData, tagsData, segmentacoes, schema = null }: VideoPlaylistsProps): JSX.Element {
  const AdsTypeGenerators = dynamic(() => import('@/components/Ads/Google/AdsTypeGenerators'), { ssr: false });

  return (
    <Layout layoutData={{ segmentacoes, tagsData, navBarData, schema }}>
      <WrapperContent isOnHeader hasBreadcrumb>
        <Breadcrumb breadcrumbList={homeData.breadcrumb} />
      </WrapperContent>
      <WrapperContent>
        <Titulo texto="Todas as Playlists" color="black" headingTag="h1" />
        <PlaylistCardList>
          {homeData.data.playlists
            .filter(
              playlist =>
                playlist.video && playlist.video.url && playlist.logo && playlist.logo.url && playlist.thumbnail && playlist.thumbnail.url && playlist.playlist
            )
            .map(playlist => (
              <PlaylistCard
                key={playlist.playlist.titulo}
                playlist={{ title: playlist.playlist.titulo, url: playlist.playlist.url }}
                video={{ title: playlist.video.titulo, description: playlist.video.descricao, url: playlist.video.url }}
                logo={playlist.logo}
                backgroundColor={playlist?.corPrincipal}
                thumbnail={playlist.thumbnail}
              />
            ))}
        </PlaylistCardList>
      </WrapperContent>
    </Layout>
  );
}

export const getServerSideProps: GetServerSideProps = async context => {
  const { host } = context.req.headers;

  const mainReq = { promise: site.get<FeaturedNewsApi>(`/video/playlists/`), link: `/video/playlists/` };
  const responseMenuReq = { promise: api.get<NavBarContentData>(`/componentes/menu/`), link: `/componentes/menu/` };

  const { promiseData, data, statusCode } = await getConcurrentPromises({
    mainPromise: mainReq,
    navBarPromise: responseMenuReq,
    host,
  });

  if (data.redirect || data.notFound) return data;
  const homeData = data;
  const navBarData: NavBarContentData = promiseData[responseMenuReq.link].data.data;
  const tagsData = data.ssr;
  const { segmentacoes } = data;

  context.res.setHeader('Cache-Control', data.headers['Cache-control']);
  context.res.setHeader('Expires', data.headers.Expires);
  context.res.statusCode = statusCode;

  return {
    props: {
      tagsData,
      homeData,
      navBarData,
      segmentacoes,
    },
  };
};
